/**
 * Token Refresh Service
 *
 * Hybrid approach for keeping tokens fresh:
 * 1. Timer-based: Proactively refresh 5 minutes before expiry (happy path)
 * 2. Check-before-call: Verify token validity before each API call (edge cases)
 *
 * This handles edge cases where timers might not fire reliably:
 * - Computer sleep/wake
 * - Browser tab throttling
 * - Page refresh
 */

import config from '../config/environments'

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000 // Refresh 5 minutes before token expires

class TokenRefreshService {
  private tokenExpiry: number | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private isRefreshing: boolean = false
  private refreshPromise: Promise<boolean> | null = null

  /**
   * Initialize token refresh tracking
   * Call this after successful login with the token expiry timestamp
   *
   * @param expiryTimestamp - Unix timestamp (seconds) when ID token expires
   */
  initialize(expiryTimestamp: number): void {
    // Convert to milliseconds if needed
    const expiryMs = expiryTimestamp > 10000000000 ? expiryTimestamp : expiryTimestamp * 1000

    this.tokenExpiry = expiryMs
    this.scheduleRefresh()

    const minutesUntilExpiry = (expiryMs - Date.now()) / 1000 / 60
    console.log(`Token refresh initialized. Token expires in ${minutesUntilExpiry.toFixed(1)} minutes`)
  }

  /**
   * Clean up timers and state
   * Call this on logout
   */
  destroy(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    this.tokenExpiry = null
    this.isRefreshing = false
    this.refreshPromise = null

    console.log('Token refresh service destroyed')
  }

  /**
   * Check if token needs refresh and refresh if necessary
   * Call this before making API calls (check-before-call strategy)
   *
   * @returns Promise<boolean> - true if token is valid (refreshed if needed), false if refresh failed
   */
  async ensureValidToken(): Promise<boolean> {
    // No expiry set - can't validate
    if (!this.tokenExpiry) {
      return true // Assume valid, let API handle auth failures
    }

    const now = Date.now()
    const timeUntilExpiry = this.tokenExpiry - now

    // Token still has plenty of time
    if (timeUntilExpiry > REFRESH_BEFORE_EXPIRY_MS) {
      return true
    }

    // Token expired or close to expiry - refresh it
    console.log(`Token expires in ${timeUntilExpiry / 1000 / 60} minutes - refreshing`)
    return await this.performRefresh()
  }

  /**
   * Get current token expiry (for debugging/monitoring)
   */
  getTokenExpiry(): number | null {
    return this.tokenExpiry
  }

  // ─────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────

  private scheduleRefresh(): void {
    if (!this.tokenExpiry) {
      return
    }

    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }

    const now = Date.now()
    const timeUntilRefresh = this.tokenExpiry - now - REFRESH_BEFORE_EXPIRY_MS

    // Token already expired or about to expire - refresh immediately
    if (timeUntilRefresh <= 0) {
      console.log('Token needs immediate refresh')
      this.performRefresh()
      return
    }

    // Schedule refresh for 5 minutes before expiry
    console.log(`Scheduling token refresh in ${timeUntilRefresh / 1000 / 60} minutes`)
    this.refreshTimer = setTimeout(() => {
      this.performRefresh()
    }, timeUntilRefresh)
  }

  private async performRefresh(): Promise<boolean> {
    // Already refreshing - return existing promise
    if (this.isRefreshing && this.refreshPromise) {
      console.log('Refresh already in progress - waiting for completion')
      return this.refreshPromise
    }

    this.isRefreshing = true

    // Create refresh promise
    this.refreshPromise = this.doRefresh()

    try {
      const result = await this.refreshPromise
      return result
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  private async doRefresh(): Promise<boolean> {
    try {
      console.log('🔄 Refreshing token...')

      const url = `${config.authApiBaseUrl}/auth/refresh`

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send cookies (refresh token)
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        console.error('❌ Token refresh failed:', response.status)

        // 401 means refresh token expired - user needs to re-login
        if (response.status === 401) {
          console.log('Refresh token expired - redirecting to login')
          window.location.href = '/login?expired=true'
        }

        return false
      }

      // Backend sets new cookies automatically
      // We need to update our stored expiry time
      const data = await response.json()

      // Backend should return new token expiry in response
      // If not provided, calculate based on standard 60-minute ID token lifetime
      const newExpiry = data.tokenExpiry
        ? (data.tokenExpiry > 10000000000 ? data.tokenExpiry : data.tokenExpiry * 1000)
        : Date.now() + (60 * 60 * 1000) // Default: 60 minutes from now

      this.tokenExpiry = newExpiry
      this.scheduleRefresh() // Schedule next refresh

      console.log('✅ Token refreshed successfully')
      return true

    } catch (error) {
      console.error('❌ Token refresh error:', error)
      return false
    }
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService()
